import {
  PricingUtils,
  PricingTier,
  SavingsCalculation,
} from "../../utils/pricing-utils";

interface PricingTranslations {
  [key: string]: string;
}

interface PlanSelectEvent {
  planId: string;
  context: "marketing" | "authenticated";
  currency: string;
  interval: string;
}

export class PricingWebComponent extends HTMLElement {
  private _context: "marketing" | "authenticated" = "marketing";
  // Currency is now derived from the tiers (each tier carries its own).
  // Kept for backward compatibility with the plan-select event payload.
  private _currency: string = "USD";
  private _interval: "monthly" | "annually" = "monthly";
  private _userPlan: number = 0;
  private _userInterval: "monthly" | "annually" = "monthly";
  private _showToggles: boolean = true;
  private _showSavings: boolean = true;
  // VAT rate as a percent (e.g. 20 for France). 0 = no tax label rendered.
  // Wrapper passes this through after looking it up by country. Pricing shown
  // to consumers is already TTC (the wrapper multiplies tier prices); this
  // attribute drives the "incl. tax (X% VAT)" label below the amount.
  private _vatRate: number = 0;
  private _theme: "dark" | "light" = "dark";
  private _translations: PricingTranslations = {};
  private _apiBaseUrl: string = "https://app.blindvalet.com";
  private _locale: string = "en";
  private _tiers: PricingTier[] = [];

  static get observedAttributes() {
    return [
      "context",
      "currency",
      "interval",
      "user-plan",
      "user-interval",
      "show-toggles",
      "show-savings",
      "theme",
      "translations",
      "api-base-url",
      "locale",
      "vat-rate",
    ];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this._tiers = PricingUtils.getDefaultConfig().tiers;
  }

  connectedCallback() {
    this.render();
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string) {
    if (oldValue === newValue) return;

    switch (name) {
      case "context":
        this._context = newValue as "marketing" | "authenticated";
        break;
      case "currency":
        this._currency = newValue;
        break;
      case "interval":
        this._interval = newValue as "monthly" | "annually";
        break;
      case "user-plan":
        this._userPlan = parseInt(newValue) || 0;
        break;
      case "user-interval":
        this._userInterval = newValue as "monthly" | "annually";
        break;
      case "show-toggles":
        this._showToggles = newValue === "true";
        break;
      case "show-savings":
        this._showSavings = newValue === "true";
        break;
      case "theme":
        this._theme = newValue as "dark" | "light";
        break;
      case "translations":
        try {
          this._translations = JSON.parse(newValue || "{}");
        } catch (e) {
          console.warn("Invalid translations JSON:", e);
          this._translations = {};
        }
        break;
      case "api-base-url":
        this._apiBaseUrl = newValue;
        break;
      case "locale":
        this._locale = newValue;
        break;
      case "vat-rate":
        this._vatRate = parseFloat(newValue) || 0;
        break;
    }
    this.render();
  }

  // Getters and setters for property access
  get context() {
    return this._context;
  }
  set context(value: "marketing" | "authenticated") {
    this._context = value;
    this.render();
  }

  get currency() {
    return this._currency;
  }
  set currency(value: string) {
    // Currency is derived from the catalog (carried on each tier). The setter
    // is preserved for backward compatibility with consumers that still set it,
    // but it no longer drives any UI — formatting reads tier.currency directly.
    this._currency = value;
    this.render();
  }

  get interval() {
    return this._interval;
  }
  set interval(value: "monthly" | "annually") {
    this._interval = value;
    this.dispatchEvent(
      new CustomEvent("intervalChange", { detail: { interval: value } })
    );
    this.render();
  }

  get translations() {
    return this._translations;
  }
  set translations(value: PricingTranslations) {
    this._translations = value;
    this.render();
  }

  get tiers() {
    return this._tiers;
  }
  set tiers(value: PricingTier[]) {
    this._tiers = value;
    this.render();
  }

  private translateText(key: string, variables?: Record<string, any>): string {
    let translation = this._translations[key] || key;

    if (variables) {
      Object.keys(variables).forEach((variable) => {
        translation = translation.replace(
          new RegExp(`{${variable}}`, "g"),
          variables[variable]
        );
      });
    }

    return translation;
  }

  private formatPrice(tier: PricingTier): string {
    if (tier.id === "kitchen-table") {
      return this.translateText("FREE", {});
    }
    return PricingUtils.getFormattedTierPrice(tier, this._interval, this._locale);
  }

  private getSavingsDisplay(tier: PricingTier): string {
    if (
      !this._showSavings ||
      this._interval !== "annually" ||
      tier.id === "kitchen-table"
    ) {
      return "";
    }
    const savings = PricingUtils.getTierSavings(tier, this._locale);
    if (!savings) return "";
    return this.translateText("PRICING_SAVINGS_MESSAGE", {
      amount: savings.formatted,
    });
  }

  private getButtonText(tier: PricingTier): string {
    if (this._context === "marketing") {
      return this.translateText("SIGNUP", {});
    }

    // Authenticated context logic
    const isCurrentPlan = tier.offer === this._userPlan;
    const isCurrentInterval = this._interval === this._userInterval;

    if (isCurrentPlan && (isCurrentInterval || tier.offer === 0)) {
      // Was "Your Plan" (disabled). Now: clickable "Stay on this plan" — the
      // event bubbles to the wrapper, which marks onboarding/last-prompt
      // timestamps and routes the user to /lobby.
      return this.translateText("STAY_ON_THIS_PLAN", {});
    }

    if (tier.offer! < this._userPlan) {
      return this.translateText("DOWNGRADE", {});
    }

    if (tier.offer! > this._userPlan) {
      return this.translateText("UPGRADE", {});
    }

    return this.translateText("SELECT", {});
  }

  private isButtonDisabled(_tier: PricingTier): boolean {
    // Buttons are always clickable now — even on the user's current tier
    // ("Stay on this plan" needs to dispatch planSelect so the wrapper can
    // record the dismissal and route to /lobby).
    return false;
  }

  private handlePlanSelect(tier: PricingTier) {
    const event: PlanSelectEvent = {
      planId: tier.id,
      context: this._context,
      currency: tier.currency || this._currency,
      interval: this._interval,
    };
    this.dispatchEvent(new CustomEvent("planSelect", { detail: event }));
  }

  private handleIntervalChange(interval: "monthly" | "annually") {
    this.interval = interval;
  }

  private getStyles(): string {
    const isDark = this._theme === "dark";

    return `
      :host {
        --pricing-primary-color: ${
          isDark ? "#f97316" : "#ea580c"
        }; /* orange-500/600 */
        --pricing-secondary-color: ${
          isDark ? "#fdba74" : "#fb923c"
        }; /* orange-300/400 */
        --pricing-accent-color: ${
          isDark ? "#fed7aa" : "#fed7aa"
        }; /* orange-200 */
        --pricing-background: ${
          isDark ? "#111827" : "#ffffff"
        }; /* gray-900/white */
        --pricing-card-background: ${
          isDark ? "rgba(255, 255, 255, 0.05)" : "#f9fafb"
        }; /* white/5 or gray-50 */
        --pricing-text-primary: ${
          isDark ? "#ffffff" : "#111827"
        }; /* white/gray-900 */
        --pricing-text-secondary: ${
          isDark ? "#9ca3af" : "#6b7280"
        }; /* gray-400/500 */
        --pricing-border-color: ${
          isDark ? "rgba(255, 255, 255, 0.1)" : "#e5e7eb"
        }; /* white/10 or gray-200 */
        --pricing-featured-border: var(--pricing-primary-color);
        
        display: block;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        color: var(--pricing-text-primary);
        background-color: var(--pricing-background);
      }

      .pricing-container {
        max-width: 1280px;
        margin: 0 auto;
        padding: 6rem 1.5rem;
      }

      .pricing-header {
        max-width: 64rem;
        margin: 0 auto;
        text-align: center;
        margin-bottom: 4rem;
      }

      .pricing-title {
        font-size: 3rem;
        font-weight: 600;
        line-height: 1.2;
        margin: 0 0 1.5rem 0;
        color: var(--pricing-text-primary);
      }

      .pricing-subtitle {
        font-size: 1.25rem;
        font-weight: 500;
        line-height: 1.6;
        color: var(--pricing-text-secondary);
        margin: 0;
        max-width: 32rem;
        margin: 0 auto;
      }

      .pricing-toggles {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1.5rem;
        margin-bottom: 2.5rem;
      }

      @media (min-width: 640px) {
        .pricing-toggles {
          flex-direction: row;
          justify-content: center;
          gap: 2rem;
        }
      }

      .toggle-group {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 0.25rem;
        background-color: var(--pricing-card-background);
        padding: 0.25rem;
        border-radius: 9999px;
        border: 1px solid var(--pricing-border-color);
      }

      .toggle-button {
        padding: 0.5rem 1rem;
        border: none;
        border-radius: 9999px;
        font-size: 0.875rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        color: var(--pricing-text-primary);
        background-color: transparent;
      }

      .toggle-button.active {
        background-color: var(--pricing-primary-color);
        color: white;
      }

      .toggle-button:hover {
        opacity: 0.8;
      }

      .pricing-cards {
        display: grid;
        grid-template-columns: 1fr;
        gap: 2rem;
        max-width: 18rem;
        margin: 0 auto;
      }

      @media (min-width: 768px) {
        .pricing-cards {
          grid-template-columns: repeat(2, 1fr);
          max-width: 36rem;
        }
      }

      @media (min-width: 1280px) {
        .pricing-cards {
          grid-template-columns: repeat(4, 1fr);
          max-width: none;
        }
      }

      .pricing-card {
        background-color: var(--pricing-card-background);
        border: 1px solid var(--pricing-border-color);
        border-radius: 1.5rem;
        padding: 2rem;
        position: relative;
        transition: all 0.2s ease;
        text-align: left;
      }
      
      .pricing-card * {
        text-align: inherit;
      }

      .pricing-card.featured {
        border-color: var(--pricing-featured-border);
        border-width: 2px;
        background-color: var(--pricing-card-background);
      }

      .pricing-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 1rem;
        text-align: left;
      }

      .pricing-card-name {
        font-size: 1.325rem;
        font-weight: 600;
        color: var(--pricing-text-primary);
        margin: 0;
      }

      .pricing-card-badge {
        background-color: var(--pricing-primary-color);
        color: white;
        padding: 0.25rem 0.75rem;
        border-radius: 9999px;
        font-size: 0.75rem;
        font-weight: 600;
      }

      .pricing-card-description {
        color: var(--pricing-text-secondary);
        font-size: 0.875rem;
        line-height: 1.5;
        margin: 0 0 1.5rem 0;
        text-align: left;
      }

      .pricing-card-price {
        margin-bottom: 1.5rem;
        text-align: left;
      }

      .pricing-card-price-amount {
        font-size: 2.25rem;
        font-weight: 600;
        color: var(--pricing-text-primary);
        margin: 0;
      }

      .pricing-card-price-period {
        color: var(--pricing-text-secondary);
        font-size: 0.875rem;
        font-weight: 600;
        margin-left: 0.25rem;
      }

      .pricing-card-tax-note {
        color: var(--pricing-text-secondary);
        font-size: 0.75rem;
        margin: 0.25rem 0 0 0;
        text-align: left;
      }

      .pricing-card-savings {
        color: var(--pricing-secondary-color);
        font-size: 0.875rem;
        margin: 0.5rem 0 0 0;
        text-align: left;
      }

      .pricing-card-savings-amount {
        font-weight: 700;
        color: var(--pricing-accent-color);
      }

      .pricing-card-button {
        width: 100%;
        padding: 0.75rem 1.5rem;
        border: none;
        border-radius: 0.5rem;
        font-size: 0.875rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        margin-bottom: 2rem;
        text-align: center;
      }

      .pricing-card-button.primary {
        background-color: var(--pricing-primary-color);
        color: white;
      }

      .pricing-card-button.primary:hover {
        opacity: 0.9;
      }

      .pricing-card-button.secondary {
        background-color: var(--pricing-card-background);
        color: var(--pricing-text-primary);
        border: 1px solid var(--pricing-border-color);
      }

      .pricing-card-button.secondary:hover {
        background-color: var(--pricing-border-color);
      }

      .pricing-card-button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .pricing-card-features {
        list-style: none;
        padding: 0;
        margin: 0;
      }

      .pricing-card-feature {
        display: flex;
        align-items: flex-start;
        gap: 0.75rem;
        margin-bottom: 0.75rem;
        color: var(--pricing-text-secondary);
        font-size: 0.875rem;
        line-height: 1.5;
        text-align: left;
      }

      .pricing-card-feature-icon {
        width: 1.25rem;
        height: 1.25rem;
        color: var(--pricing-primary-color);
        flex-shrink: 0;
      }

      .pricing-card-feature-text {
        flex: 1;
      }

      .pricing-card-feature-badge {
        display: inline-block;
        margin-left: 0.5rem;
        padding: 0.125rem 0.5rem;
        background: rgba(6, 182, 212, 0.18);
        color: #67e8f9;
        border: 1px solid rgba(6, 182, 212, 0.4);
        border-radius: 0.375rem;
        font-size: 0.7rem;
        font-weight: 600;
        line-height: 1;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        vertical-align: middle;
      }

      .pricing-card-feature:last-child {
        margin-bottom: 0;
      }

      /* Hide component if no tiers */
      .pricing-container:empty {
        display: none;
      }
    `;
  }

  private render() {
    if (!this.shadowRoot) return;

    const styles = this.getStyles();

    this.shadowRoot.innerHTML = `
      <style>${styles}</style>
      <div class="pricing-container">
        <div class="pricing-header">
          <h2 class="pricing-title">${this.translateText(
            "PRICING_SECTION_TITLE"
          )}</h2>
          <p class="pricing-subtitle">${this.translateText(
            "PRICING_SECTION_SUBTITLE"
          )}</p>
        </div>
        
        ${
          this._showToggles
            ? `
          <div class="pricing-toggles">
            <div class="toggle-group">
              <button class="toggle-button ${
                this._interval === "monthly" ? "active" : ""
              }" data-interval="monthly">
                ${this.translateText("MONTHLY_BILLING")}
              </button>
              <button class="toggle-button ${
                this._interval === "annually" ? "active" : ""
              }" data-interval="annually">
                ${this.translateText("ANNUAL_BILLING")}
              </button>
            </div>
          </div>
        `
            : ""
        }
        
        <div class="pricing-cards">
          ${this._tiers
            .map(
              (tier) => `
            <div class="pricing-card ${tier.featured ? "featured" : ""}">
              <div class="pricing-card-header">
                <h3 class="pricing-card-name">${tier.name}</h3>
              </div>
              
              <p class="pricing-card-description">${this.translateText(
                tier.description || ""
              )}</p>
              
              <div class="pricing-card-price">
                <div class="pricing-card-price-amount">
                  ${this.formatPrice(tier)}
                  ${
                    tier.id !== "kitchen-table"
                      ? `
                    <span class="pricing-card-price-period">
                      ${this._interval === "annually" ? "/year" : "/month"}
                    </span>
                  `
                      : ""
                  }
                </div>
                ${
                  this._vatRate > 0 && tier.id !== "kitchen-table"
                    ? `
                  <p class="pricing-card-tax-note">
                    ${this.translateText("INCL_TAX")} (${this._vatRate}% VAT)
                  </p>
                `
                    : ""
                }

                ${
                  this.getSavingsDisplay(tier)
                    ? `
                  <p class="pricing-card-savings">
                    ${this.getSavingsDisplay(tier)}
                  </p>
                `
                    : ""
                }
              </div>
              
              <button 
                class="pricing-card-button ${
                  tier.featured ? "primary" : "secondary"
                }"
                data-tier-id="${tier.id}"
                ${this.isButtonDisabled(tier) ? "disabled" : ""}
              >
                ${this.getButtonText(tier)}
              </button>
              
              <ul class="pricing-card-features">
                ${tier.features
                  .map((feature) => {
                    // Convention: a feature key may carry a "|beta" suffix to
                    // attach a Beta badge after the label. Parse here so the
                    // catalog stays as a flat string array.
                    const [key, marker] = String(feature).split("|");
                    const text = this.translateText(key);
                    const badge =
                      marker === "beta"
                        ? `<span class="pricing-card-feature-badge">${this.translateText("BETA")}</span>`
                        : "";
                    return `
                  <li class="pricing-card-feature">
                    <svg class="pricing-card-feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span class="pricing-card-feature-text">${text}${badge}</span>
                  </li>
                `;
                  })
                  .join("")}
              </ul>
            </div>
          `
            )
            .join("")}
        </div>
      </div>
    `;

    // Add event listeners
    this.addEventListeners();
  }

  private addEventListeners() {
    if (!this.shadowRoot) return;

    // Interval toggle listeners (currency toggle has been removed — currency
    // is now derived from the catalog, fixed by the user's country)
    this.shadowRoot.querySelectorAll("[data-interval]").forEach((button) => {
      button.addEventListener("click", (e) => {
        const interval = (e.target as HTMLElement).dataset.interval as
          | "monthly"
          | "annually";
        this.handleIntervalChange(interval);
      });
    });

    // Plan select listeners
    this.shadowRoot.querySelectorAll("[data-tier-id]").forEach((button) => {
      button.addEventListener("click", (e) => {
        const tierId = (e.target as HTMLElement).dataset.tierId;
        const tier = this._tiers.find((t) => t.id === tierId);
        if (tier && !this.isButtonDisabled(tier)) {
          this.handlePlanSelect(tier);
        }
      });
    });
  }
}

// Register the custom element
customElements.define("pricing-component", PricingWebComponent);
